import { Elements } from '@stripe/react-stripe-js';
import classNames from 'classnames';
import { memo, useCallback, useMemo } from 'react';

import { useFeature } from '@features/FeatureToggle';
import {
  CardAnnotation,
  CheckoutSelector,
  LearnMoreList,
  type TpStripePlanCardProps,
  useGetStripePromise,
} from '@features/Stripe';
import { FormattedMessage } from '@features/intl';
import { Typography } from '@mentalgrowth/ui-kit-web';
import { currencyReplacer } from 'src/utils/payment/currency-replacer';
import { SubscriptionCardMaster } from '../master';
import { CurrentPrice } from '../shared/current-price';
import { DiscountPercentage } from '../shared/discount-percentage';
import { FullPrice } from '../shared/full-price';
import { PriceUnitFull } from '../shared/price-unit-full';
import { UsersCompare } from '../shared/users-compare';

import IconChevronDown from '../../../../../../public/icons/chevron-down.svg';

import 'react-loading-skeleton/dist/skeleton.css';
import styles from './index.module.css';

export const NewCombo = memo((props: TpStripePlanCardProps) => {
  const { inputIdPrefix, plan, wasPaymentTried, planCardClassname } = props;

  const stripePromise = useGetStripePromise();
  const isIntroOfferEnabled = useFeature('introOffer');

  const currencySymbol = currencyReplacer(plan?.currency);

  const dataMemoized = useMemo(
    () =>
      isIntroOfferEnabled
        ? {
            billingPeriod: plan?.introOfferData?.billingPeriod,
            billingPeriodUnit: plan?.introOfferData?.billingPeriodUnit,
            fullPrice: plan?.introOfferData?.fullPrice,
            fullPriceDiscount: plan?.introOfferData?.fullPriceDiscount,
            discountPercentage: plan?.introOfferData?.discountPercentage,
          }
        : {
            billingPeriod: plan?.billingPeriod,
            billingPeriodUnit: plan?.billingPeriodUnit,
            fullPrice: plan?.fullPrice,
            fullPriceDiscount: plan?.fullPriceDiscount,
            discountPercentage: plan?.discountPercentage,
          },
    [
      isIntroOfferEnabled,
      plan?.introOfferData,
      plan?.fullPrice,
      plan?.fullPriceDiscount,
      plan?.billingPeriodUnit,
      plan?.billingPeriod,
      plan?.discountPercentage,
    ],
  );

  const renderPaymentComponent = useCallback(
    () => (
      <Elements stripe={stripePromise}>
        <CheckoutSelector isCompactStyle />
      </Elements>
    ),
    [stripePromise],
  );

  return (
    <SubscriptionCardMaster
      {...props}
      descriptionEndView
      inputIdPrefix={inputIdPrefix}
      planCardClassname={classNames(styles.planCard, planCardClassname)}
      contentClassname={styles.content}
      leftBottomContent={(value: boolean) => (
        <div className={styles.wrapper}>
          <div
            className={classNames(styles.text, {
              [styles.textChecked]: value,
            })}
          >
            {plan?.fractionalPrice !== plan?.fractionalPriceDiscount && (
              <FullPrice
                currencySymbol={currencySymbol}
                price={dataMemoized.fullPrice}
              />
            )}{' '}
            <FullPrice
              price={dataMemoized.fullPriceDiscount}
              currencySymbol={currencySymbol}
              withDiscount
            />{' '}
            per{' '}
            <span className={styles.startPeriod}>
              {dataMemoized.billingPeriod} {dataMemoized.billingPeriodUnit}
            </span>
          </div>

          <div className={styles.discountPercentage}>
            {(plan?.discountPercentage ?? 0) > 0 && (
              <DiscountPercentage
                discountPercentage={plan.discountPercentage ?? 0}
                isActive={value}
                wasPaymentTried={wasPaymentTried}
                size="medium"
              />
            )}
          </div>
        </div>
      )}
      rightContent={(value: boolean) => (
        <div className={styles.alignEnd}>
          <div className={styles.rightBlockContainer}>
            <div className={styles.fullPriceAndDiscount}>
              {plan?.fractionalPrice !== plan?.fractionalPriceDiscount && (
                <PriceUnitFull
                  plan={plan}
                  checked={value}
                  className={styles.priceUnitFull}
                />
              )}
            </div>
            <CurrentPrice plan={plan} checked={value} />
          </div>
        </div>
      )}
      fullWidthBottomContent={(value: boolean) => (
        <div className={styles.bottomContent}>
          {value ? (
            <div className={styles.payments}>
              {plan?.learnMoreList && (
                <LearnMoreList
                  learnMoreList={plan.learnMoreList}
                  variant="secondary"
                />
              )}
              {plan?.usersCompareBadge && <UsersCompare />}
              {renderPaymentComponent()}
              {isIntroOfferEnabled && (
                <CardAnnotation
                  planName={plan.name}
                  fullPriceDiscount={plan.fullPriceDiscount}
                  currency={plan.currency}
                />
              )}
            </div>
          ) : (
            <div className={styles.learnMoreHeader}>
              <Typography as="p" variant="body2-bold">
                <FormattedMessage
                  id="Onboarding.Paywall.Main.Subtitle"
                  defaultMessage="Learn more"
                />
              </Typography>
              <IconChevronDown
                className={classNames(styles.collapseIcon, {
                  [styles.unCollapsed]: value,
                })}
              />
            </div>
          )}
        </div>
      )}
    />
  );
});
